import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfigurationService, CoursesService, PathsService } from '@draeger-academy/common';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { MyCourseItem } from '../../../../../../models/myCourse';

@Component({
    selector: 'da-account-courses-list',
    templateUrl: './account-courses-list.component.html',
    styleUrls: ['./account-courses-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccountCoursesListComponent implements OnInit, OnDestroy {
    public active = false;
    public categoryName = '';
    public coursesPerPage = 2;
    public allCategories;
    public coursesLink: string;
    public translationPrefix = 'myCourses';
    public isUserPage = true;
    private _$courses: BehaviorSubject<MyCourseItem[]> = new BehaviorSubject<MyCourseItem[]>(null);
    private _$selectedValue: BehaviorSubject<string> = new BehaviorSubject('0');
    private _$showMoreInformation: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$coursesFiltered: BehaviorSubject<MyCourseItem[]> = new BehaviorSubject<MyCourseItem[]>(null);
    private _$coursesFilteredMore: BehaviorSubject<MyCourseItem[]> = new BehaviorSubject<MyCourseItem[]>(null);
    private _$categories: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);
    private _$categoriesForFilter: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);
    private _$testResults: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);
    private _subscriptionSiteSettings: Subscription;
    private _subscriptionQueryParams: Subscription;
    private _subscriptionCourses: Subscription;
    private _subscriptionCategories: Subscription;
    private subModuleCourseList: string[];

    constructor(
        private coursesService: CoursesService,
        private activatedRoute: ActivatedRoute,
        public _translateService: TranslateService,
        private _configurationService: ConfigurationService,
        private _pathsService: PathsService
    ) {}

    public get showFilter$(): Observable<boolean> {
        return of(this._$coursesFiltered.getValue()?.length > 0 && JSON.stringify(this._$categories.getValue()) !== JSON.stringify({}));
    }

    public get coursesFiltered$(): Observable<MyCourseItem[]> {
        return this._$coursesFiltered.asObservable();
    }

    public get coursesFilteredMore$(): Observable<MyCourseItem[]> {
        return this._$coursesFilteredMore.asObservable();
    }

    public get showMoreInformation$(): Observable<boolean> {
        return this._$showMoreInformation.asObservable();
    }

    public get categories$(): Observable<{}> {
        return this._$categories.asObservable();
    }
    public get categoriesForFilter$(): Observable<{}> {
        return this._$categoriesForFilter.asObservable();
    }

    public get testResults$(): Observable<{}> {
        return this._$testResults.asObservable();
    }

    public get courses$(): Observable<{}> {
        return this._$courses.asObservable();
    }

    ngOnDestroy() {
        if (this._subscriptionSiteSettings) {
            this._subscriptionSiteSettings.unsubscribe();
        }
        if (this._subscriptionQueryParams) {
            this._subscriptionQueryParams.unsubscribe();
        }
        if (this._subscriptionCourses) {
            this._subscriptionCourses.unsubscribe();
        }
        if (this._subscriptionCategories) {
            this._subscriptionCategories.unsubscribe();
        }
    }

    ngOnInit() {
        this.coursesLink = this._pathsService.getCourseListLink();
        if (this._configurationService.isPartnerPage()) {
            this.translationPrefix = 'account.myCourses';
        }
        this._getCategories().then((cat) => {
            if (cat) {
                this.allCategories = cat;
                this.categoryName = cat[0].name;
                this._$categories.next(cat);
                const newCategories = [];
                cat.forEach((catItem) => {
                    if (catItem.id !== 'CANCELLED' && catItem.id !== 'PROVISIONAL') {
                        newCategories.push(catItem);
                    }
                });
                this._$categoriesForFilter.next(newCategories);
                this.getCourses();
            }
        });

        this._getTestResults().then((res) => {
            if (res) {
                this._$testResults.next(res);
            }
        });

        this._subscriptionCategories = this.coursesService.languageSelected?.subscribe((language) => {
                this._getCategories().then((cat) => {
                    if (cat) {
                        this.allCategories = cat;
                        this.categoryName = cat[0].name;
                        this._$categories.next(cat);
                        const newCategories = [];
                        cat.forEach((catItem) => {
                            if (catItem.id !== 'CANCELLED' && catItem.id !== 'PROVISIONAL') {
                                newCategories.push(catItem);
                            }
                        });
                        this._$categoriesForFilter.next(newCategories);
                        this._getTestResults().then((res) => {
                            if (res) {
                                this._$testResults.next(res);
                                this.getCourses();
                            }
                        });
                    }
                });
        });
    }

    public getCourses() {
        this.coursesService.getUserCourses().then(
            (courses) => {
                if (courses.status && courses.status === 404) {
                    this._$courses.next([]);
                    this._$coursesFiltered.next([]);
                } else if (courses && courses.length > 0) {
                    this._$courses.next(courses);
                    this.onSelect('0');
                    this._$selectedValue.next('0');
                } else {
                    this._$courses.next([]);
                    this._$coursesFiltered.next([]);
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }

    public onSelect(categoryId) {
        if (!(categoryId instanceof Event)) {
            this._$showMoreInformation.next(false);
            let filteredMore = [];
            let coursesFiltered = [];
            this._$courses.getValue().filter((course) => {
                const currentDate = new Date();
                const endDate = new Date(course.endDate);
                let courseIsInFuture = false;
                if (endDate >= currentDate) {
                    courseIsInFuture = true;
                } else {
                    courseIsInFuture = false;
                }
                if (categoryId === 'BOOKED') {
                    if (course.status === categoryId) {
                        filteredMore = [];
                        coursesFiltered.push(course);
                    }
                } else if (categoryId === 'WAITINGLIST') {
                    if (course.isProduct && course.status === categoryId) {
                        filteredMore = [];
                        coursesFiltered.push(course);
                    } else if (!course.isProduct && course.status === categoryId && courseIsInFuture) {
                        filteredMore = [];
                        coursesFiltered.push(course);
                    }
                } else if (categoryId === 'CANCELLED') {
                    if (course.status === categoryId) {
                        filteredMore = [];
                        coursesFiltered.push(course);
                    }
                } else if (categoryId === 'OFFEN') {
                    if (course.isProduct && course.testResult !== 'PASSED') {
                        filteredMore = [];
                        coursesFiltered.push(course);
                    } else if (!course.isProduct && (course.status === 'OFFEN' || course.status === 'BOOKED') && courseIsInFuture) {
                        filteredMore = [];
                        coursesFiltered.push(course);
                    }
                } else if (categoryId === 'PASSED') {
                    if (course.isProduct && course.testResult === 'PASSED' && course.status === 'BOOKED') {
                        filteredMore = [];
                        coursesFiltered.push(course);
                    } else if (!course.isProduct && !courseIsInFuture && course.status === 'BOOKED') {
                        filteredMore = [];
                        coursesFiltered.push(course);
                    }
                }
            });

            if (categoryId.toString() === '0') {
                this._$selectedValue.next('0');
                if (this._configurationService.$coursesPerPage) {
                    this._subscriptionCourses = this._configurationService.$coursesPerPage.subscribe((data) => {
                        if (data) {
                            this.coursesPerPage = Number(data);
                        }
                    });
                }
                filteredMore = this._$courses.getValue().slice(this.coursesPerPage, this._$courses.getValue().length);
                coursesFiltered = this._$courses.getValue().slice(0, this.coursesPerPage);
            }

            this.allCategories.filter((data) => {
                if (data.id === categoryId) {
                    this.categoryName = data.name;
                }
            });

            this._$coursesFilteredMore.next([...filteredMore]);
            this._$coursesFiltered.next(coursesFiltered);
            this.filterCoursesNoSubmoduleCoursesForCourseFiltered(this._$coursesFiltered.getValue(), this._$courses.getValue());
            this.filterCoursesNoSubmoduleCoursesForCourseFilteredMore(this._$coursesFilteredMore.getValue(), this._$courses.getValue());

        }
    }

    public showMore($event: boolean) {
        this._$showMoreInformation.next($event);
    }

    private _getCategories(): Promise<any> {
        return new Promise((resolve, reject) => {
            this._translateService.stream('courses.courseStates').subscribe((courseStates) => {
                if (typeof courseStates !== 'string' ) {
                    if (!this._$categoriesForFilter.getValue()) {
                        resolve(courseStates);
                    } else {
                        if (this._$categoriesForFilter.getValue()[0].name !== courseStates[0].name) {
                            resolve(courseStates);
                        }
                    }
                }
            });
        });
    }
    private _getTestResults(): Promise<any> {
        return new Promise((resolve, reject) => {
            this._translateService.stream('courses.testResults').subscribe((testResults) => {
                if (typeof testResults !== 'string') {
                    resolve(testResults);
                }
            });
        });
    }

    private filterCoursesNoSubmoduleCoursesForCourseFiltered(courses, originCourses) {
        this.subModuleCourseList = [];
        const courseFiltered = [];
        this.filterCoursesNoSubmoduleCourses(originCourses)

        courses.forEach((course) => {
            if (this.subModuleCourseList.indexOf(course.id) === -1 ) {
                courseFiltered.push(course);
            }
        });
        this._$coursesFiltered.next(courseFiltered);
    }

    private filterCoursesNoSubmoduleCoursesForCourseFilteredMore(courses, originCourses) {
        this.subModuleCourseList = [];
        const courseFilteredMore = [];

        this.filterCoursesNoSubmoduleCourses(originCourses)
        courses.forEach((course) => {
            if (this.subModuleCourseList.indexOf(course.id) === -1 ) {
                courseFilteredMore.push(course);
            }
        });
        this._$coursesFilteredMore.next(courseFilteredMore);
    }

    private filterCoursesNoSubmoduleCourses(originCourses){
        originCourses.forEach((course) => {
            if (course.infoPackageAvailable && course.infoPackageSubModuleIds.length > 0){
                course.infoPackageSubModuleIds.forEach((subModuleCourseId) => {
                    this.subModuleCourseList.push(subModuleCourseId);
                });
            }
        });
    }
}
